RivalIQ.apps.Favorites.module('models', function (models, App, Backbone, Marionette, $, _) {

    'use strict';

    models.Metric = Backbone.Model.extend({
        // Parse a metric definition into the decomposed structure used for
        // editing. Specifically, we're going to pull out the display properties
        // from `metric` (e.g., showProgressBar, showSparkline) and make a clean
        // metric attribute in the object returned.
        parse: function (metric, options) {
            const me = this;

            let attrs = _.pick(metric, 'channel', 'timeAnalysisMode');

            attrs.metric = RivalIQ.metric.subtract(metric, options.baseMetric);

            if (options.widget) {
                attrs = me.applyWidgetRestrictions(options.widget, attrs);
            }

            me.source = RivalIQ.metric.search.getPropertyValue(metric, 'source');

            if (options.source) {
                me.source = options.source;
            }

            if (!_.isUndefined(metric.showProgressBar)) {
                attrs.showProgressBar = Boolean(metric.showProgressBar);
            }

            if (!_.isUndefined(metric.customSummary)) {
                attrs.customSummary = metric.customSummary;
            }

            if (!_.isUndefined(metric.companyIdAggregate)) {
                attrs.companyIdAggregate = metric.companyIdAggregate;
            }

            // I'm converting showSparkline to be a DAILY_VALUES metric for the purposes of the builder
            // as we don't have a separate "showSparkline" control (instead, we jam it into the time analysis
            // mode picker). The inverse of this operation is in toJSON in this file.
            if (metric.showSparkline) {
                delete attrs.metric.showSparkline;
                if (attrs.timeAnalysisMode === RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD) {
                    attrs.timeAnalysisMode = RivalIQ.enums.timeAnalysisMode.DAILY_VALUES;
                }
            }

            return attrs;
        },

        /*
         *  Apply any widget specific metric restrictions to the parsed metric
         */
        applyWidgetRestrictions: function (widget, attrs) {
            attrs = _.clone(attrs);

            if (!widget.isPropValueValid('timeAnalysisMode', attrs.timeAnalysisMode)) {
                attrs.timeAnalysisMode = widget.getValidPropValues('timeAnalysisMode')[0];
            }

            return attrs;
        },

        // Setting the channel can force a change to both metric and
        // timeAnalysisMode
        setChannel: function (source, baseMetric) {
            const me = this;

            const metric = me.getMetric(source);
            me.source = source;

            const channel = RivalIQ.data.channels.get(source).getChannel();
            me.set({
                channel: channel,
                metric: metric,
                timeAnalysisMode: me.getTimeAnalysisMode(metric)
            });

            me._ensureSummaryAggregatesAreValid(baseMetric);
        },

        // Setting the metric can force a change to timeAnalysisMode
        setMetric: function (metric, baseMetric, defaultCustomSummary) {
            const me = this;

            me.set({
                metric: metric,
                timeAnalysisMode: me.getTimeAnalysisMode(metric)
            });

            me._ensureSummaryAggregatesAreValid(baseMetric, defaultCustomSummary);
        },

        setTimeAnalysisMode: function (tam, baseMetric) {
            const me = this;

            me.set({
                timeAnalysisMode: tam
            });

            me._ensureSummaryAggregatesAreValid(baseMetric);
        },

        setShowProgressBar: function (showProgressBar) {
            const me = this;
            me.set({
                showProgressBar: showProgressBar
            });
        },

        getShowProgressBar: function () {
            const me = this;
            return me.get('showProgressBar');
        },

        setCustomSummary: function (customSummary) {
            const me = this;
            me.set({
                customSummary: customSummary
            });
        },

        getCustomSummary: function () {
            const me = this;
            return me.get('customSummary');
        },

        setCompanyIdAggregate: function (companyIdAggregate) {
            const me = this;
            me.set({
                companyIdAggregate: companyIdAggregate
            });
        },

        getCompanyIdAggregate: function () {
            const me = this;
            return me.get('companyIdAggregate');
        },

        // Get updated metric on source change.
        getMetric: function (source) {
            const me = this;

            if (source === me.source) {
                return me.get('metric');
            }

            const sourceMetrics = RivalIQ.data.metrics.forSource(source);
            const current = me.get('metric');
            const idx = RivalIQ.metric.findIndex(sourceMetrics, current, {
                fuzzy: true
            });

            return sourceMetrics[(idx === -1 ? 0 : idx)];
        },

        getTimeAnalysisMode: function (metric) {
            const me = this;
            const tam = me.get('timeAnalysisMode');

            if (_.isUndefined(tam)) {
                return tam;
            }

            const tams = RivalIQ.metric.validation.validTimeAnalysisModes(metric);
            if (!_.includes(tams, tam)) {
                return tams[0];
            }

            return tam;
        },

        // This method is the inverse of `parse` -- we need to flatten the display
        // properties and the metric into one object. We'd probably be better off
        // if we persisted everything in the separated object.
        toJSON: function () {
            const me = this;

            // NOTE if a `timeAnalysisMode` is not specified at all we should
            // default to `MAIN_PERIOD`
            let timeAnalysisMode = me.get('timeAnalysisMode');
            if (!timeAnalysisMode && !_.has(me.attributes, 'timeAnalysisMode')) {
                timeAnalysisMode = RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD;
            }

            const defaults = {
                channel: me.get('channel'),
                timeAnalysisMode: timeAnalysisMode
            };

            if (me.get('timeAnalysisMode') === RivalIQ.enums.timeAnalysisMode.DAILY_VALUES) {
                defaults.showSparkline = true;
                defaults.timeAnalysisMode = RivalIQ.enums.timeAnalysisMode.MAIN_PERIOD;
            }

            return _.defaults(defaults, me.get('metric'));
        },

        toMetric: function (baseMetric) {
            const me = this;
            const metric = RivalIQ.metric.build(_.omit(me.toJSON(), 'customSummary'), baseMetric, {
                prependDimensions: true
            });

            metric.companyIdAggregate = me.getCompanyIdAggregate();
            return metric;
        },

        clone: function () {
            const me = this;
            const model = new me.constructor(_.cloneDeep(me.attributes));
            model.source = me.source;
            model.attributes.showProgressBar = undefined;

            return model;
        },

        validTimeAnalysisModes: function (baseMetric) {
            const me = this;
            return RivalIQ.metric.validation.validTimeAnalysisModes(me.toMetric(baseMetric));
        },

        _ensureSummaryAggregatesAreValid: function (baseMetric, defaultCustomSummary) {
            const me = this;

            const currCustomSummary = me.getCustomSummary();
            if (currCustomSummary) {
                const validOptions = me.validCustomSummaryOptions(baseMetric);

                const isStillValid = _.find(validOptions, {
                    id: currCustomSummary
                });
                if (!isStillValid) {
                    me.setCustomSummary(defaultCustomSummary || null);
                }
            }

            const companyIdAggregate = me.getCompanyIdAggregate();
            if (companyIdAggregate) {
                const validOptions = me.validDimensionAggregationOptions(baseMetric);

                if (_.isEmpty(validOptions)) {
                    me.setCompanyIdAggregate(null);
                } else {
                    const isStillValid = _.find(validOptions, {
                        id: companyIdAggregate
                    });
                    if (!isStillValid) {
                        me.setCompanyIdAggregate(_.first(validOptions).id);
                    }
                }
            }
        },

        validCustomSummaryOptions: function (baseMetric) {
            const me = this;

            const metric = RivalIQ.metric.build(baseMetric, _.clone(me.get('metric')));

            const isPrivate = RivalIQ.enums.sourceDetails[me.getSource()].privateData;
            const hasAggregateAccess = !isPrivate || RivalIQ.state.plan.get('has_multi_handle_private_advanced');
            const isNumeric = RivalIQ.metric.property.isNumeric(metric);

            let options = [];
            if (hasAggregateAccess && isNumeric) {

                const additionalOptions = [
                    `${RivalIQ.enums.benchmarkCompaniesFilters.all_companies.id}_average`,
                    `${RivalIQ.enums.benchmarkCompaniesFilters.all_companies.id}_median`
                ];

                metric.source = me.getSource();
                const isSummable = new RivalIQ.models.MetricDefinition(metric).isSummable();
                const timeAnalysisMode = me.get('timeAnalysisMode');
                if (isSummable && timeAnalysisMode !== RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE) {
                    additionalOptions.push(`${RivalIQ.enums.benchmarkCompaniesFilters.all_companies.id}_sum`);
                }

                options = options.concat(_.map(additionalOptions, id => {
                    return {
                        id: id,
                        name: _.find(RivalIQ.enums.benchmarkCompareAgainstOptions, {
                            id: id
                        }).name
                    };
                }));
            }

            return options;
        },

        validDimensionAggregationOptions: function (baseMetric) {
            const me = this;

            const metric = RivalIQ.metric.build(baseMetric, _.clone(me.get('metric')));
            const isNumeric = RivalIQ.metric.property.isNumeric(metric);

            if (isNumeric) {

                const timeAnalysisMode = me.get('timeAnalysisMode');

                // NOTE: this will have to be updated when we add more dimensions to aggregate over.
                const items = [{
                    id: 'companiesAverage',
                    name: 'Company Set Average'
                }];

                // Median is not available for Delta Relatives.
                if (timeAnalysisMode !== RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE) {
                    items.push([{
                        id: 'companiesMedian',
                        name: 'Company Set Median'
                    }]);
                }

                metric.source = me.getSource();
                const isSummable = new RivalIQ.models.MetricDefinition(metric).isSummable();
                if (isSummable && timeAnalysisMode !== RivalIQ.enums.timeAnalysisMode.DELTA_RELATIVE) {
                    items.push({
                        id: 'companiesSum',
                        name: 'Company Set Sum'
                    });
                }

                return items;
            } else {
                return [];
            }
        },

        getSource: function () {
            const me = this;
            return me.source;
        },

        isSourceAvailable: function () {
            const me = this;
            const source = me.getSource();
            const validChannels = me.collection.getValidChannels();
            return Boolean(validChannels.get(source));
        }
    });
});
